import { i18n, type MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { z } from 'zod';

import type { ActionActivity, AgentText, Permission, ReadAction } from '@/shared/screen/types';

// Arquivo leve (entra nos `*.screen.ts`, que estão no JS inicial): só zod, Lingui e tipos do manifesto.
// O motor que grava no formulário é o `useAgentFill` (`agent-fill.ts`), com a tela montada (U56, L2).

/** Um campo que o Luy pode escrever: o zod explícito e enxuto do valor, no formato do fio (U37-8), e o rótulo. */
export interface FillFieldDefinition {
  readonly schema: z.ZodType;
  readonly label: MessageDescriptor;
}

export type FillFields = Readonly<Record<string, FillFieldDefinition>>;

/** O que o Luy manda: só os campos que quer preencher, cada um validado pelo zod dele. */
export type FillInput<F extends FillFields> = { readonly [K in keyof F]?: z.infer<F[K]['schema']> };

/** A ordem de aplicação e os rótulos, que o motor lê na tela montada. */
export interface FillSpec {
  readonly fields: readonly { readonly name: string; readonly label: MessageDescriptor }[];
}

export interface FillActionDefinition<F extends FillFields> {
  readonly id: string;
  /** Padrão: "Preencher com o Luy". */
  readonly label?: MessageDescriptor;
  /** Para o modelo (U46): o que dá para preencher e o que continua com a pessoa. */
  readonly description: AgentText;
  readonly permission?: Permission;
  readonly fields: F;
  /** Ordem de aplicação, dependências primeiro (produto → padrões → campos). Padrão: a ordem de `fields`. */
  readonly order?: readonly (keyof F & string)[];
  /** O que a ação de fachada diz sem a tela montada. */
  readonly mountedMessage?: MessageDescriptor;
  /** A linha do chat enquanto preenche (padrão: "Preenchendo os campos"); terminada, ela vira "Preencheu N campos". */
  readonly activity?: ActionActivity;
}

export type FillAction<F extends FillFields = FillFields> = ReadAction<FillInput<F>> & { readonly fill: FillSpec };

/**
 * Ação `draft` de preencher o formulário da tela a partir de um spec (U56): zod explícito por campo, todos opcionais,
 * e um `run` de fachada que a tela troca pelo do `useAgentFill(...).bind(action)`.
 */
export function defineFillAction<F extends FillFields>(definition: FillActionDefinition<F>): FillAction<F> {
  const names: readonly (keyof F & string)[] = definition.order ?? Object.keys(definition.fields);
  const fields = names.map((name) => ({ name, field: definition.fields[name] as FillFieldDefinition }));
  const shape = Object.fromEntries(fields.map(({ name, field }) => [name, field.schema.optional()]));
  const input = z
    .strictObject(shape)
    // eslint-disable-next-line lingui/no-unlocalized-strings -- resposta ao modelo, inglês canônico (U33)
    .refine((value) => Object.values(value).some((item) => item !== undefined), { message: 'Pass at least one field to fill.' });
  const mounted = definition.mountedMessage ?? msg`Abra a tela do formulário para o Luy preencher.`;
  return {
    id: definition.id,
    kind: 'draft',
    label: definition.label ?? msg`Preencher com o Luy`,
    description: definition.description,
    activity: definition.activity ?? { running: msg`Preenchendo os campos`, done: msg`Campos preenchidos` },
    ...(definition.permission ? { permission: definition.permission } : {}),
    input: input as unknown as z.ZodType<FillInput<F>>,
    fill: { fields: fields.map(({ name, field }) => ({ name, label: field.label })) },
    run: () => {
      throw new Error(i18n._(mounted));
    },
  };
}
